import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  cartRequestPayloadSelector,
  multiRoomCartDataSelector,
  occupancyRoomsSelector as multiRoomMPRLSelector,
  multiRoomArcInfo,
  isCartUpdatingStatusSelector,
  isEntirePropertySelector,
  // isEntirePropertySelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import splitRoomString from '@stencil/hotels-utils/src/split-room-string';
import {roomStringSelectorFn} from '@stencil/hotels-common-reducer/src/selectors';
import {defaultTheme} from '@stencil/build-theme';
import HappyCloseIcon from '@stencil/icons/src/HappyCloseIcon/HappyCloseIcon';

import {
  ButtonBase,
  PrimaryLink,
  BaseColumnWrap,
} from '../../../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';

import HappyAdultIcon from '@stencil/icons/src/HappyAdultIcon/HappyAdultIcon';
import PaxInfo from '../Shared/PaxInfo/PaxInfo';
import AddUpgradeRoomFlow from '../Shared/AddUpgradeRoomFlow/AddUpgradeRoomFlow';
import * as S from './MultiRoomPaxModal.styles';
import {fetchCartUpdatedPriceDataThunk} from '@stencil/hotels-cg-reducer/src/details/thunks';
import {
  getCgRPCRoomsPaxCount,
  getMultiRoomKey,
  getRoomsandPaxCountInCartCg,
  setARCObjCg,
} from '@stencil/hotels-utils/src/common-utils';
import PWIList from '../Shared/PWIList/PWIList';
import {updateArcReachedForRoomCode} from '@stencil/hotels-cg-reducer/src/details/actions';
import MealPlanAndRefundInfo from '../Shared/MealPlanAndRefundInfo/MealPlanAndRefundInfo';
import {appConfig, gaConfig} from '@stencil/hotels-config';
import {
  RoomCase,
  getCancellationPolicyColor,
  multiRoomTextCreator,
} from '@stencil/hotels-utils/src/details-utils';

// Omniture Imports
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';

export default function MultiRoomPaxModal({
  roomPriceCode,
  roomTypeCode,
  roomName,
  pwiData = [],
  roomData = {},
  isShowModal = true,
  roomDefaultImage = '',
  isNoInclusions = false,
  onCloseClick = () => {},
  propertyTypeTag = '',
}) {
  const {multiRoomDefaultNoImagePlacholder} = appConfig;
  const wrapperRef = useRef(null);
  const mprlData = useSelector(multiRoomMPRLSelector);
  const requestData = useSelector(cartRequestPayloadSelector);
  const cartUIData = useSelector(multiRoomCartDataSelector);
  const arcInfoObj = useSelector(multiRoomArcInfo);
  const isCartUpdating = useSelector(isCartUpdatingStatusSelector);
  let roomString = useSelector(roomStringSelectorFn);
  const isEntireProperty = useSelector(isEntirePropertySelector);
  const {roomCount, paxCount, childCount: totalChildCount} = splitRoomString(roomString);
  const [newRequestObj, setNewRequestObj] = useState(requestData);
  const [newCartUItObj, setNewCartUItObj] = useState(cartUIData);
  const [newArcInfoObj, setNewArcInfoObj] = useState(arcInfoObj);
  const [isCountClicked, setIsCountClicked] = useState(false);
  const sellableType = roomData?.sellableType;

  const dispatch = useDispatch();

  const {isRoomExists, totalRooms, adultCount, childCount} = getCgRPCRoomsPaxCount(
    newCartUItObj,
    roomPriceCode,
  );

  const checkARCReached = reqObject => {
    let obj = setARCObjCg(newArcInfoObj, roomTypeCode, mprlData, reqObject);
    setNewArcInfoObj(obj);
    dispatch(updateArcReachedForRoomCode(obj));
  };

  const checkTotalGuestReached = roomUIObject => {
    const {totalGuestCount} = getRoomsandPaxCountInCartCg(roomUIObject);
    const isTotalGuestCountExceeded = totalGuestCount > paxCount + totalChildCount;
    return isTotalGuestCountExceeded;
  };

  const updateRoomPaxObj = (key, data, index) => {
    let requestObj = {},
      cartUIObj = {},
      isTotalGuestCountExceeded = false;
    requestObj = isShowModal ? {...newRequestObj} : {...requestData};
    cartUIObj = isShowModal ? {...newCartUItObj} : {...cartUIData};

    if (data) {
      requestObj[key] = data;
      cartUIObj[key] = {
        count: data?.count,
        roomData,
        multiRoomPwi: roomData?.tariffs?.[index],
      };
    } else {
      delete requestObj[key];
      delete cartUIObj[key];
    }
    isTotalGuestCountExceeded = checkTotalGuestReached(cartUIObj);
    if (!isTotalGuestCountExceeded) {
      checkARCReached(requestObj);
      setNewRequestObj(requestObj);
      setNewCartUItObj(cartUIObj);
      if (!isShowModal) {
        setIsCountClicked(true);
      }
    }
    return isTotalGuestCountExceeded;
  };

  const updateCart = () => {
    dispatch(
      fetchCartUpdatedPriceDataThunk({
        apiVersion: 'v13',
        flavor: 'v3',
        requestData: newRequestObj,
        cartUIData: newCartUItObj,
        arcLimitData: newArcInfoObj,
      }),
    );
  };

  const getRoomOccupanyCount = () => {
    let tpl = null;
    if (isRoomExists) {
      tpl = (
        <>
          <div>
            <S.GuestListInnerText>
              {multiRoomTextCreator({
                type: RoomCase.AddFlowButton,
                sellableType,
                isEntireProperty,
                totalRoomCount: totalRooms,
                propertyTypeTag: propertyTypeTag,
              })}
            </S.GuestListInnerText>
          </div>
          <div>
            Fits {adultCount + childCount} Guests out of {paxCount + totalChildCount} guests
          </div>
        </>
      );
    } else {
      tpl = null;
    }
    return tpl;
  };

  const handleClickOutside = event => {
    if (isShowModal) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onCloseClick();
      }
    }
    return;
  };

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    /**This is when Pax Variant is not shown in Modal
     * Instead displayed directly in Guests & Rooms Column,
     * then directly invoke the cart-discount API
     * **/
    if (!isShowModal && isCountClicked) {
      updateCart();
      setIsCountClicked(false);
    }
  }, [newRequestObj, newCartUItObj]);
  console.log('roomData::', roomData);
  const getMealPlanConfig = () => {
    let config = {};
    config['text'] = roomData?.tariffPersuasion?.mealPersuasion?.[0]?.persuasionText;
    // roomData?.amenities?.map(item => {
    //   if (item?.category != 'MEAL') return;
    //   config['text'] = item?.text;
    // });
    return config;
  };
  return (
    <>
      <S.MultiRoomModalOverLaySection ref={wrapperRef}>
        <S.OverlayHeaderDiv>
          <S.FlexItemCenter>
            <S.HotelImage
              src={roomDefaultImage ? roomDefaultImage : multiRoomDefaultNoImagePlacholder}
            />
            <S.RoomTypSpan>{roomData?.roomTypeNameData}</S.RoomTypSpan>
          </S.FlexItemCenter>
          <PrimaryLink onClick={() => onCloseClick()}>
            <HappyCloseIcon
              width="1.6rem"
              height="1.6rem"
              fill={defaultTheme.colors.downriver.base}
            />
          </PrimaryLink>
        </S.OverlayHeaderDiv>
        <S.MainContentWrapper>
          <li>
            <MealPlanAndRefundInfo
              mealPlanData={{
                // t: getMealPlanConfig()?.text,
                t: getMealPlanConfig()?.text,
                tc: '',
              }}
              refundPlanData={{
                t:
                  roomData?.cancellationPolicy?.type == 'FC'
                    ? 'Free Cancellation'
                    : 'Non Refundable',
                tc: getCancellationPolicyColor(roomData?.cancellationPolicy?.type),
              }}
              paddingTop={'0px'}
            />
          </li>

          {roomData?.tariffs?.map((item, index) => {
            return (
              <li>
                <PWIList
                  pwiDataItem={item}
                  index={index}
                  roomPriceCode={roomPriceCode}
                  roomTypeCode={roomTypeCode}
                  supplierCode={roomData?.supplierCode}
                  onRoomCountUpdate={(key, data, index) => updateRoomPaxObj(key, data, index)}
                />
              </li>
            );
          })}

          <li>
            <S.GuestListDiv>
              <S.GuestListInnerWrapper>{getRoomOccupanyCount()}</S.GuestListInnerWrapper>

              <S.DoneBtn
                disabled={isCartUpdating}
                onClick={() => {
                  updateCart();
                  onCloseClick();
                  dispatch(
                    omnitureThunk(
                      omnitureConfig.pages.DetailsUnif,
                      omnitureConfig.events.ctaClick,
                      {
                        cta: {
                          name: `${DetailsPageComponents.AddRoomFlow}_${detailsClick.multiRoomEventActions.Add_room_clicked}_${totalRooms}_added`,
                          type: omnitureConfig.ctaType.Button,
                          componentName: DetailsPageComponents.AddRoomFlow,
                          destination: '',
                        },
                      },
                    ),
                  );
                }}
              >
                Done
              </S.DoneBtn>
            </S.GuestListDiv>
          </li>
        </S.MainContentWrapper>
      </S.MultiRoomModalOverLaySection>
    </>
  );
}
